'use client'

import { useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { ContentGate } from '@/components/content/content-gate'
import { PortableTextRenderer } from '@/components/content/portable-text-renderer'
import { Comments } from '@/components/content/comments'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import type { BenchComponentProps } from './bench-types'
import { BenchMediaPane, stepKey } from './bench-media-pane'
import { BenchColumns } from './bench-columns'
import { NotationGuideModal } from './bench-notation-guide'
import { useBenchProgress } from './use-bench-progress'
import {
  TitleBar,
  ResumeButton,
  GatherFirst,
  RopeConsumptionTable,
  RopeWrapTable,
  WhereYoullTieIt,
  BenchToolsRow,
} from './bench-parts'

export function FloggerBenchComponent(props: BenchComponentProps) {
  const {
    title,
    typeLabel,
    abbreviation,
    difficulty,
    isGated,
    hasAccess,
    isAuthenticated,
    body,
    bodyLang,
    vimeoId,
    vimeoMinutes,
    stepGroups,
    ropeNeeded,
    ropeConsumption,
    ropeWrap,
    whereYoullTieIt,
    isTool,
    contentId,
    userId,
    commentsEnabled,
    pageUrl,
    progressKey,
    notationSlugs,
  } = props

  const { doneSteps, toggleStep } = useBenchProgress(progressKey)
  const [activeGroup, setActiveGroup] = useState(0)
  const [activeStep, setActiveStep] = useState(0)
  const [notationOpen, setNotationOpen] = useState(false)
  const t = useTranslations('floggerBench')
  const benchRef = useRef<HTMLDivElement>(null)

  const totalSteps = useMemo(
    () => stepGroups.reduce((n, g) => n + g.steps.length, 0),
    [stepGroups],
  )
  const totalDone = doneSteps.size
  const hasProgress = totalDone > 0

  // All reference sections start expanded (multi-open accordion), matching the
  // design page — the accordion just lets the reader collapse what they don't need.
  const openSections = ['overview', 'rope-consumption', 'rope-wrap', 'rope']

  const resume = () => {
    // First unchecked step across groups.
    for (let g = 0; g < stepGroups.length; g++) {
      for (let s = 0; s < stepGroups[g].steps.length; s++) {
        if (!doneSteps.has(stepKey(g, s))) {
          setActiveGroup(g)
          setActiveStep(s)
          benchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          return
        }
      }
    }
    benchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <article style={{ background: '#FAFAFC' }}>
      <TitleBar
        typeLabel={typeLabel}
        title={title}
        difficulty={difficulty}
        isGated={isGated}
        right={
          hasProgress && totalSteps > 0 ? (
            <ResumeButton label={t('resume', { done: totalDone, total: totalSteps })} onClick={resume} />
          ) : undefined
        }
      />

      <ContentGate isGated={isGated} hasAccess={hasAccess} isAuthenticated={isAuthenticated}>
        <BenchColumns
          storageKey="theduchy.bench.reference-collapsed"
          initialCollapsed={props.railInitialCollapsed}
          gridRef={benchRef}
          media={(collapsed) => (
            <BenchMediaPane
              variant="component"
              collapsed={collapsed}
              stepGroups={stepGroups}
              vimeoId={vimeoId}
              vimeoMinutes={vimeoMinutes}
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              doneSteps={doneSteps}
              toggleStep={toggleStep}
              onOpenNotation={() => setNotationOpen(true)}
            />
          )}
          reference={
            <>
              <BenchToolsRow
                contentId={contentId}
                userId={userId}
                pageUrl={pageUrl}
                title={title}
              />

              <Accordion defaultValue={openSections}>
                {!!body?.length && (
                  <AccordionItem value="overview" className="border-[#EEEFF4]">
                    <AccordionTrigger className="text-[15px] font-bold text-[#111827]">
                      {t('overview')}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="prose prose-sm max-w-none prose-a:text-[color:var(--brand-700)]">
                        <PortableTextRenderer value={body as never} lang={bodyLang ?? null} />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {!isTool && ropeConsumption && ropeConsumption.length > 0 && (
                  <AccordionItem value="rope-consumption" className="border-[#EEEFF4]">
                    <AccordionTrigger className="text-[15px] font-bold text-[#111827]">
                      {t('ropeConsumption')}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[13px]" style={{ color: '#5F647A' }}>
                        {t('ropeConsumptionNote')}
                      </p>
                      <RopeConsumptionTable rows={ropeConsumption} />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {!isTool && ropeWrap && ropeWrap.length > 0 && (
                  <AccordionItem value="rope-wrap" className="border-[#EEEFF4]">
                    <AccordionTrigger className="text-[15px] font-bold text-[#111827]">
                      {t('ropeWrap')}
                    </AccordionTrigger>
                    <AccordionContent>
                      <RopeWrapTable rows={ropeWrap} />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {ropeNeeded.length > 0 && (
                  <AccordionItem value="rope" className="border-[#EEEFF4]">
                    <AccordionTrigger className="text-[15px] font-bold text-[#111827]">
                      {t('ropeNeeded')}
                    </AccordionTrigger>
                    <AccordionContent>
                      <GatherFirst items={ropeNeeded} />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </>
          }
        />
      </ContentGate>

      <WhereYoullTieIt
        cards={whereYoullTieIt}
        abbreviation={abbreviation}
        heading={isTool ? t('neededFor') : t('whereYoullTieIt')}
      />

      {commentsEnabled && (
        <section className="px-6 pb-12">
          <div className="mx-auto max-w-[1280px]">
            {/* Comments brings its own mt-12 border-t + "Comments (N)" header,
                so this wrapper adds no separator/top padding of its own. */}
            <Comments contentId={contentId} userId={userId} />
          </div>
        </section>
      )}

      <NotationGuideModal
        open={notationOpen}
        onClose={() => setNotationOpen(false)}
        slugs={notationSlugs}
      />
    </article>
  )
}
